import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useKeycloak } from "../providers/keycloak-provider";
import SimplePagination from "../components/SimplePagination";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DashboardLayout, { LayoutProps } from "../components/DashboardLayout";

export interface Feriado {
  id?: number;
  nome: string;
  data: string; // "YYYY-MM-DD"
  tipo: string;
  recorrente: boolean;
}

const TIPOS = ["Nacional", "Estadual", "Municipal", "Outro"];
const EMPTY: Feriado = { nome: "", data: "", tipo: "Nacional", recorrente: false };

function fmtData(data: string, recorrente: boolean): string {
  if (!data) return "-";
  const [y, m, d] = data.split("-");
  return recorrente ? `${d}/${m}` : `${d}/${m}/${y}`;
}

interface Props extends LayoutProps {
  feriadosPath: string;
}

const PER_PAGE_OPTIONS = [10, 25, 50];

export default function FeriadosPage({ feriadosPath, csrfToken, flash }: Props) {
  const { keycloak } = useKeycloak();
  const [feriados, setFeriados]   = useState<Feriado[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState<Feriado | null>(null);
  const [form, setForm]           = useState<Feriado>(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Feriado | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Filters & pagination
  const [search, setSearch]       = useState("");
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(PER_PAGE_OPTIONS[0]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return feriados.filter(f => q ? f.nome.toLowerCase().includes(q) : true);
  }, [feriados, search]);

  const pagedFeriados = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page, perPage]);

  const handleSearchChange = (q: string) => { setSearch(q); setPage(1); };
  const handlePerPageChange = (n: number) => { setPerPage(n); setPage(1); };

  const headers = {
    "Content-Type": "application/json",
    "X-CSRF-Token": csrfToken,
    "Authorization": `Bearer ${keycloak.token ?? ""}`,
  };

  useEffect(() => {
    axios.get<Feriado[]>(feriadosPath, {
      headers: { Authorization: `Bearer ${keycloak.token ?? ""}` },
    })
      .then((r) => { setFeriados(r.data); setLoading(false); })
      .catch(() => { setError("Erro ao carregar feriados."); setLoading(false); });
  }, [feriadosPath]);

  function openNew() {
    setEditing(null); setForm(EMPTY); setFormError(null); setShowForm(true);
  }
  function openEdit(f: Feriado) {
    setEditing(f); setForm({ ...f }); setFormError(null); setShowForm(true);
  }
  function closeForm() {
    setShowForm(false); setEditing(null); setForm(EMPTY); setFormError(null);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setFormError(null);
    try {
      if (editing?.id) {
        const res = await axios.patch<Feriado>(`/api/v1/admin/feriados/${editing.id}`, { feriado: form }, { headers });
        setFeriados(prev => prev.map(f => f.id === res.data.id ? res.data : f));
      } else {
        const res = await axios.post<Feriado>("/api/v1/admin/feriados", { feriado: form }, { headers });
        setFeriados(prev => [...prev, res.data]);
      }
      closeForm();
    } catch (err: any) {
      const msgs: string[] = err?.response?.data?.errors ?? ["Erro ao salvar. Tente novamente."];
      setFormError(msgs.join(", "));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/v1/admin/feriados/${deleteTarget.id}`, { headers });
      setFeriados(prev => prev.filter(x => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  }

  if (loading) {
    return (
      <DashboardLayout flash={flash}>
        <div className="flex items-center justify-center py-24 text-gray-400 gap-3">
          <svg className="animate-spin w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-sm">Carregando feriados...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) return (
    <DashboardLayout flash={flash}>
      <div className="py-16 text-center"><p className="text-red-500 text-sm">{error}</p></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout flash={flash}>
    <div className="max-w-6xl mx-auto space-y-2">
    <div className=" p-6 border-b border-gray-100 bg-gray-50 ">
        <header >      
             
          <div className="w-full flex items-between gap-6 flex-wrap"> 

        <div className="flex justify-between items-center w-full">
            <h1 className="text-2xl font-bold text-gray-800 ">
              Feriados           
            </h1>
        <button onClick={openNew}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors flex-shrink-0 ml-4 cursor-pointer">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Feriado
          </button>
        </div>         

            <div className="flex items-start gap-3 bg-blue-50/50 border-l-4 border-blue-400 p-4 rounded-r-lg">  
              <div className="flex-shrink-0 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-700 block md:inline mb-1 md:mb-0">
                  Gestão de Feriados:
                </span> 
                {" "}Exiba e gerencie os feriados cadastrados. Você pode adicionar feriados à 
                lista utilizando o botão "Novo Feriado", editar ou excluir feriados existentes, e usar os filtros para encontrar feriados específicos por nome. Feriados recorrentes (que se repetem todo ano) podem ser identificados facilmente na lista.
              </p>
            </div>
          </div>      
        <div className="flex-[3] max-w-[340px] justify-self-end">
              <label className="text-[11px] font-semibold text-gray-400 mb-1.5 ml-1">
                Buscar 
              </label>
              <input
                type="search"
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Ex: Natal, Tiradentes, etc..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white
                focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
              />
               
        </div>
         
        </header>    
    </div>
      
      {/* ── Table card ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {feriados.length === 0 ? (
          <div className="py-16 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-400 text-sm">Nenhum feriado cadastrado.</p>
            <button onClick={openNew} className="text-blue-600 text-sm hover:underline mt-1">
              Cadastrar primeiro feriado
            </button>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50 text-gray-600 text-left">
                  <th className="px-4 py-3 font-semibold border-b border-gray-200">Nome</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-200">Data</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-200">Tipo</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">Recorrente</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-200 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedFeriados.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-400">
                      Nenhum feriado corresponde aos filtros.
                    </td>
                  </tr>
                ) : pagedFeriados.map(f => (
                <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">{f.nome}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {fmtData(f.data, f.recorrente)}
                    {f.recorrente && <span className="ml-1 text-xs text-blue-500">(todo ano)</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      f.tipo === "Nacional" ? "bg-blue-100 text-blue-700"
                      : f.tipo === "Estadual" ? "bg-purple-100 text-purple-700"
                      : f.tipo === "Municipal" ? "bg-yellow-100 text-yellow-700"
                      : "bg-gray-100 text-gray-600"
                    }`}>
                      {f.tipo || "Outro"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {f.recorrente
                      ? <span className="inline-flex items-center justify-center w-5 h-5 bg-green-100 text-green-600  text-xs font-bold">✓</span>
                      : <span className="inline-flex items-center justify-center w-5 h-5 bg-gray-100 text-gray-400 rounded-full text-xs">–</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                
                      <Button onClick={() => openEdit(f)} className="text-white" variant="edit" size="sm">Editar</Button>
                      <Button onClick={() => setDeleteTarget(f)} className="text-white" variant="destructive" size="sm">Excluir</Button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
            <div className="px-4 border-t border-gray-100">
              <SimplePagination
                totalItems={filtered.length}
                itemsPerPage={perPage}
                currentPage={page}
                onPageChange={setPage}
                onPerPageChange={handlePerPageChange}
                perPageOptions={PER_PAGE_OPTIONS}
              />
            </div>
          </>
        )}
      </div>

      {/* Dialog de exclusão */}
      <Dialog open={deleteTarget !== null} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Excluir feriado</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o feriado <strong>&#34;{deleteTarget?.nome}&#34;</strong>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" className="cursor-pointer hover:text-gray-600" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
              Cancelar
            </Button>
            <Button variant="destructive" className="hover:text-white" size="sm" onClick={handleDeleteConfirm} disabled={deleteLoading}>
              {deleteLoading ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de edição/criação */}
      <Dialog open={showForm} onOpenChange={open => { if (!open) closeForm(); }}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="font-bold">{editing ? "Editar Feriado" : "Novo Feriado"}</DialogTitle>
          </DialogHeader>

          {formError && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded-lg text-sm">{formError}</div>
          )}

          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input type="text" required placeholder="Ex: Natal, Tiradentes..."
                value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
              <input type="date" required value={form.data} onChange={e => setForm({ ...form, data: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <p className="text-xs text-gray-400 mt-1">Para feriados recorrentes, o ano é ignorado.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <input id="recorrente" type="checkbox" checked={form.recorrente}
                onChange={e => setForm({ ...form, recorrente: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <label htmlFor="recorrente" className="text-sm text-gray-700">
                Recorrente (repete todo ano no mesmo dia/mês)
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" className="cursor-pointer hover:text-gray-600"  onClick={closeForm} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" size="sm" disabled={saving} className="bg-green-700 hover:bg-green-600 text-white">
                {saving ? "Salvando..." : editing ? "Salvar Alterações" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
</div>
    </DashboardLayout>
  );
}
